/**
 * Lesson OUTCOME — was the student there? (Attendance arc, Phase A,
 * card fwMubDlBzniA3JQtbUGi, 2026-09-01.)
 *
 * The session record (`lessons/occ_<slotId>_<originDate>`, minted on demand by
 * ensureLessonForOccurrence) already carries `status` — did the lesson RUN
 * (scheduled → in-progress → completed, the Lesson Dashboard timer). This adds
 * the second axis on the SAME record, so there is no new collection and the
 * `origin` join Today already performs is the attendance join:
 *
 *   outcome:     'attended' | 'no_show' | 'cancelled_family' | 'cancelled_cyv' | null
 *   outcomeAt    server timestamp of the last mark
 *   outcomeBy    uid of the marker
 *   outcomeNote  optional, unused by Phase A's UI
 *
 * Three rules from Anthony, 2026-09-01, all load-bearing:
 *   - A no-show earns nothing (it is the zero-notice case of the 24-hour rule).
 *   - `cancelled_cyv` is ADMIN ONLY — it is the mark that grants a makeup
 *     credit (Phase C), matching core.md's "admin grant, reason
 *     cyv_cancellation". A teacher who believes a no-show was the studio's
 *     fault marks no_show and tells an admin. Enforced in firestore.rules as
 *     well as here: gating a control is not gating an action.
 *   - An unmarked lesson stays null and is COUNTED, never presumed attended.
 *
 * The legacy `attendanceStatus` vocabulary (Student Profile → lesson history)
 * was never written to a single document in 1,534 and is retired by this
 * module rather than left as a second language.
 */

import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { ensureLessonForOccurrence } from './lessonService';

export const OUTCOME = Object.freeze({
	ATTENDED: 'attended',
	NO_SHOW: 'no_show',
	CANCELLED_FAMILY: 'cancelled_family',
	CANCELLED_CYV: 'cancelled_cyv',
});

// Presentation + policy per value. `tone` is a .ps-badge modifier; every one
// used here exists in portalSurface.css (success / danger / neutral / warning).
export const OUTCOME_META = Object.freeze({
	[OUTCOME.ATTENDED]: { label: 'Attended', tone: 'success', icon: 'fa-check', adminOnly: false },
	[OUTCOME.NO_SHOW]: { label: 'No-show', tone: 'danger', icon: 'fa-user-xmark', adminOnly: false },
	[OUTCOME.CANCELLED_FAMILY]: { label: 'Cancelled · family', tone: 'neutral', icon: 'fa-calendar-xmark', adminOnly: false },
	[OUTCOME.CANCELLED_CYV]: { label: 'Cancelled · CYV', tone: 'warning', icon: 'fa-building-circle-xmark', adminOnly: true },
});

// The order the control offers them in: the common case first, the
// admin-only one last.
export const OUTCOME_ORDER = Object.freeze([
	OUTCOME.ATTENDED,
	OUTCOME.NO_SHOW,
	OUTCOME.CANCELLED_FAMILY,
	OUTCOME.CANCELLED_CYV,
]);

export const isOutcome = (v) => Object.prototype.hasOwnProperty.call(OUTCOME_META, v);

/**
 * May this user change a record's outcome FROM `from` TO `to`?
 * `to === null` clears. Both directions of cancelled_cyv are admin-only: a
 * teacher may neither set it nor un-set an admin's mark. Mirrors the rules
 * clause on `lessons` update.
 */
export function canChangeOutcome({ from = null, to = null, isAdmin = false }) {
	if (to !== null && !isOutcome(to)) return false;
	if (isAdmin) return true;
	// Re-affirming the value already stored is a no-op, and must stay legal for
	// a teacher: the timer writes `status` on this same doc without touching
	// outcome, and the rules clause reads the whole post-write document.
	if (from === to) return true;
	if (to === OUTCOME.CANCELLED_CYV) return false;
	if (from === OUTCOME.CANCELLED_CYV && to !== OUTCOME.CANCELLED_CYV) return false;
	return true;
}

/** The outcomes a user may offer as choices, in display order. */
export const outcomeChoices = ({ isAdmin = false } = {}) =>
	OUTCOME_ORDER.filter((v) => isAdmin || !OUTCOME_META[v].adminOnly);

/**
 * What the Status chip reads. The OUTCOME wins over the clock: once someone has
 * said what happened, "Past" or "Completed" (both derived from the time of day)
 * are no longer the most useful thing to show. `clock` is getTimeStatus()'s
 * { text, tone } and is returned unchanged when there is no outcome.
 */
export function outcomeChip(outcome, clock) {
	if (outcome && isOutcome(outcome)) {
		const m = OUTCOME_META[outcome];
		return { text: m.label, tone: m.tone, icon: m.icon, isOutcome: true };
	}
	return { text: clock?.text ?? '', tone: clock?.tone ?? 'neutral', icon: null, isOutcome: false };
}

/**
 * The chip for a lesson whose time has gone by with nothing on the record.
 * It used to read the clock's "Completed", which was a guess about the time
 * of day presented as a fact about the lesson: Vee Port's 09-29 lesson read
 * "Completed" on Today while her record had no completion and no attendance.
 * Eight characters on purpose, the same width as "Attended", because the
 * status cell is measured to the pixel (see the chip comment in TodayPage).
 */
export const UNMARKED_CLOCK = Object.freeze({ text: 'Unmarked', tone: 'warning' });

/**
 * Is this row one a human still owes an answer for? A LESSON row (never an
 * event or trial) with no outcome, on a past date — or today, once its start
 * time has gone by. Future lessons are never unmarked; nothing has happened.
 */
export function isUnmarked(row, { isToday, isFuture, clockStatus }) {
	if (!row || row.kind !== 'lesson') return false;
	if (row.outcome) return false;
	if (isFuture) return false;
	if (isToday) return clockStatus === 'completed';
	return true;
}

/**
 * Write an outcome for a Today row. Mints the session record first when the
 * row has none (same deterministic id the Start button uses, so a later Start
 * opens this very doc). `value === null` clears the mark.
 *
 * Returns the doc id so the caller can patch its local row.
 */
export async function setLessonOutcome({ row, value, uid, date, extra = null }) {
	if (value !== null && !isOutcome(value)) throw new Error(`Unknown outcome: ${value}`);
	let id = row.lessonDocId;
	if (!id) {
		if (!row.slotId || !row.originDate) throw new Error('This row has no session to mark');
		id = await ensureLessonForOccurrence({
			slotId: row.slotId, originDate: row.originDate, date,
			startTime: row.time, duration: row.duration, studentId: row.studentId,
			studentName: row.studentName, instrument: row.instrument,
			location: row.location, isMakeup: row.isMakeup,
		});
	}
	const patch = value === null
		? { outcome: deleteField(), outcomeAt: deleteField(), outcomeBy: deleteField() }
		: { outcome: value, outcomeAt: serverTimestamp(), outcomeBy: uid || null };
	// `extra` rides the SAME write (an admin's taughtBy on Lessons Taught), so
	// the taught-by trigger never sees the mark without it.
	await updateDoc(doc(db, 'lessons', id), extra ? { ...patch, ...extra } : patch);
	return id;
}
